import React, { useCallback, useMemo, useState } from 'react';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import { Box, Typography } from '@mui/material';
import Lightbox from 'yet-another-react-lightbox';

import {
  ImageDeleteButton,
  imageDeleteRevealStyles,
} from '../../../components/ImageDeleteButton';
import {
  TitleImageIndicator,
  titleImageIndicatorRevealStyles,
} from '../../../components/TitleImageIndicator';
import { ImageThumbnail } from '../../../components/design-system';
import { FormattedMessage, useIntl } from 'react-intl';

const IMAGE_DELIVERY_URL = 'https://imagedelivery.net/3zdDZwDRXumMqC2LPbSSXw';
const THUMBNAIL_SIZE = 180;
const animationSettings = { fade: 150, swipe: 0 };
const noImagesIconStyles = { mr: 1, verticalAlign: 'middle' };
const galleryStyles = {
  display: 'grid',
  gridTemplateColumns: `repeat(auto-fill, ${THUMBNAIL_SIZE}px)`,
  justifyContent: 'start',
  gap: 1,
};
const thumbnailWrapperStyles = {
  ...imageDeleteRevealStyles,
  ...titleImageIndicatorRevealStyles,
  position: 'relative',
  cursor: 'pointer',
};

interface SiteImageTileProps {
  imageId: string;
  index: number;
  isMain: boolean;
  readOnly: boolean;
  actionsDisabled: boolean;
  onDelete: (imageId: string) => void;
  onOpen: (index: number) => void;
  onSetMain: (imageId: string) => void;
}

const SiteImageTile = React.memo(
  ({
    imageId,
    index,
    isMain,
    readOnly,
    actionsDisabled,
    onDelete,
    onOpen,
    onSetMain,
  }: SiteImageTileProps) => {
    const intl = useIntl();
    const handleOpen = useCallback(() => onOpen(index), [index, onOpen]);
    const handleDelete = useCallback(() => onDelete(imageId), [
      imageId,
      onDelete,
    ]);
    const handleSetMain = useCallback(() => onSetMain(imageId), [
      imageId,
      onSetMain,
    ]);
    const mainButtonLabel = isMain
      ? intl.formatMessage({
          id: 'site.images.mainImage',
          defaultMessage: 'Main image',
        })
      : intl.formatMessage(
          {
            id: 'site.images.setMain',
            defaultMessage: 'Set image {imageId} as main image',
          },
          { imageId }
        );

    return (
      <ImageThumbnail
        alt={intl.formatMessage(
          {
            id: 'site.images.imageAlt',
            defaultMessage: 'Site image {imageId}',
          },
          { imageId }
        )}
        onClick={handleOpen}
        size={THUMBNAIL_SIZE}
        src={`${IMAGE_DELIVERY_URL}/${imageId}/w=360`}
        sx={thumbnailWrapperStyles}
      >
        {!readOnly && (
          <>
            <TitleImageIndicator
              disabled={actionsDisabled}
              onSelect={handleSetMain}
              selected={isMain}
              title={mainButtonLabel}
            />
            <ImageDeleteButton
              disabled={actionsDisabled}
              onDelete={handleDelete}
            />
          </>
        )}
      </ImageThumbnail>
    );
  }
);

interface SiteImageGalleryProps {
  images: string[];
  mainImage?: string | null;
  readOnly: boolean;
  actionsDisabled: boolean;
  onDelete: (imageId: string) => void;
  onSetMain: (imageId: string) => void;
}

const SiteImageGallery = React.memo(
  ({
    images,
    mainImage,
    readOnly,
    actionsDisabled,
    onDelete,
    onSetMain,
  }: SiteImageGalleryProps) => {
    const intl = useIntl();
    const [index, setIndex] = useState(-1);
    const slides = useMemo(
      () =>
        images.map((imageId) => ({
          src: `${IMAGE_DELIVERY_URL}/${imageId}/public`,
        })),
      [images]
    );
    const handleOpen = useCallback((nextIndex: number) => {
      setIndex(nextIndex);
    }, []);
    const handleClose = useCallback(() => setIndex(-1), []);
    const lightboxLabels = useMemo(
      () => ({
        Close: intl.formatMessage({
          id: 'action.close',
          defaultMessage: 'Close',
        }),
        Previous: intl.formatMessage({
          id: 'image.previous',
          defaultMessage: 'Previous image',
        }),
        Next: intl.formatMessage({
          id: 'image.next',
          defaultMessage: 'Next image',
        }),
      }),
      [intl]
    );

    if (images.length === 0) {
      return (
        <Typography my={2} color="text.secondary">
          <TipsAndUpdatesIcon sx={noImagesIconStyles} />
          <FormattedMessage
            id="image.noneUploadedYet"
            defaultMessage="No images uploaded yet."
          />
        </Typography>
      );
    }

    return (
      <>
        <Box sx={galleryStyles}>
          {images.map((imageId, imageIndex) => (
            <SiteImageTile
              key={imageId}
              imageId={imageId}
              index={imageIndex}
              isMain={imageId === mainImage}
              readOnly={readOnly}
              actionsDisabled={actionsDisabled}
              onDelete={onDelete}
              onOpen={handleOpen}
              onSetMain={onSetMain}
            />
          ))}
        </Box>
        <Lightbox
          animation={animationSettings}
          index={index}
          slides={slides}
          labels={lightboxLabels}
          open={index >= 0}
          close={handleClose}
        />
      </>
    );
  }
);

export { SiteImageGallery };
